import { Component, OnInit, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { ClientListService } from '../service/client-list.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';

export interface Consumer {
  id: string;          // coffer id
  client_id: string;   // record id — what the history APIs key off
  name: string;
  email: string;
  phone: string | null;
  country?: string;
  account_activation_date: string | null;
  last_login: string | null;
  terms?: any;        // {} when not signed; date string or object with date when signed
  status: string;
  plan?: string;
  account_type?: string;
}

@Component({
    selector: 'app-client-list',
    templateUrl: './client-list.component.html',
    styleUrls: ['./client-list.component.css'],
    standalone: false
})
export class ClientListComponent implements OnInit {

  // ─── Data ─────────────────────────────────────────────────────────────────
  allClients: Consumer[] = [];
  filteredData: Consumer[] = [];
  dataSource: Consumer[] = [];

  // ─── State ────────────────────────────────────────────────────────────────
  isLoading: boolean = false;

  // ─── Search ───────────────────────────────────────────────────────────────
  searchQuery: string = '';

  // ─── Sort ─────────────────────────────────────────────────────────────────
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' | '' = '';
  accountTypeFilter: string = 'all';

  // ─── Pagination ───────────────────────────────────────────────────────────
  pageIndex: number = 0;
  pageSize: number = 10;

  activeMenuUid: string | null = null;
  selectedClient: any = null;
  showAccountTypeModal = false;
  selectedAccountType = '';
  role: string = '';

  constructor(
    private clientListService: ClientListService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private router: Router
  ) {}

  @HostListener('document:click')
  closeMenu(): void {
    this.activeMenuUid = null;
  }

  ngOnInit(): void {
    this.role = localStorage.getItem("role") || '';
    // console.log('role',this.role)

    // default to Client tab
    this.accountTypeFilter = this.role === 'SuperUser' ? 'all' : 'client';
    this.loadClients();
  }

  // ═══════════════════════════════════════════════════════════
  //  API
  // ═══════════════════════════════════════════════════════════

  // loadClients(): void {
  //   this.isLoading = true;
  //   this.spinner.show();

  //   this.clientListService.getConsumers().subscribe(
  //     (response: any) => {
  //       if (response && Array.isArray(response.data)) {
  //         this.allClients = response.data.filter(
  //           (item: Consumer) => (item.plan || '').toLowerCase() === 'lexiz'
  //         );
  //       } else {
  //         this.allClients = [];
  //       }
  //       this.applyFilter();
  //       this.isLoading = false;
  //       this.spinner.hide();
  //     },
  //     (error: any) => {
  //       console.error('Error loading clients:', error);
  //       this.toastr.error('Failed to load clients', 'Error');
  //       this.allClients = [];
  //       this.dataSource = [];
  //       this.isLoading = false;
  //       this.spinner.hide();
  //     }
  //   );
  // }

  loadClients(): void {
    this.isLoading = true;
    this.spinner.show();
    

    const accountType =
      this.accountTypeFilter === 'test'
        ? 'internal_test'
        : this.accountTypeFilter;

        console.log('Selected Filter:', accountType);


    this.clientListService.getConsumers(accountType).subscribe(
      (response: any) => {
        console.log('API Response:', response.data);

        if (response && Array.isArray(response.data)) {
          this.allClients = response.data.filter(
            (item: Consumer) =>
              (item.plan || '').toLowerCase() === 'lexiz'
          );
        } else {
          this.allClients = [];
        }

        this.applyFilter();
        this.isLoading = false;
        this.spinner.hide();
      },
      (error: any) => {
        console.error('Error loading clients:', error);
        this.toastr.error('Failed to load clients', 'Error');
        this.allClients = [];
        this.dataSource = [];
        this.isLoading = false;
        this.spinner.hide();
      }
    );
  }

  // ═══════════════════════════════════════════════════════════
  //  FILTER & SORT
  // ═══════════════════════════════════════════════════════════

  selectAccountType(type: string): void {
  this.accountTypeFilter = type;
  //this.loadClients();
  this.applyFilter();
  }

  applyFilter(): void {
    const q = this.searchQuery.trim().toLowerCase();

    let filtered = [...this.allClients];

    if (this.accountTypeFilter !== 'all') {

      const accountType =
        this.accountTypeFilter === 'test'
          ? 'internal_test'
          : this.accountTypeFilter;

      filtered = filtered.filter(
        c =>
          (c.account_type || '').toLowerCase() ===
          accountType.toLowerCase()
      );
    }

    if (q) {
      filtered = filtered.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.phone || '').toLowerCase().includes(q) ||
        (c.status || '').toLowerCase().includes(q) ||
        (c.country || '').toLowerCase().includes(q)
      );
    }

    this.filteredData = filtered;
    this.pageIndex = 0;

    if (this.sortColumn && this.sortDirection) {
      this.applySort();
    } else {
      this.dataSource = [...this.filteredData];
    }
  }

  applySort(): void {
    if (!this.sortColumn || this.sortDirection === '') {
      this.dataSource = [...this.filteredData];
      return;
    }

    const data = [...this.filteredData];
    const dir  = this.sortDirection === 'asc' ? 1 : -1;
    const col  = this.sortColumn;

    data.sort((a, b) => {
      // Date columns
      if (col === 'account_activation_date' || col === 'last_login') {
        const tA = (a as any)[col] ? new Date((a as any)[col]).getTime() : 0;
        const tB = (b as any)[col] ? new Date((b as any)[col]).getTime() : 0;
        return (tA - tB) * dir;
      }

      // Terms — sort by resolved date string (empty object sorts to bottom)
      if (col === 'terms') {
        const tA = this.resolveTermsDate(a.terms) ? new Date(this.resolveTermsDate(a.terms)!).getTime() : 0;
        const tB = this.resolveTermsDate(b.terms) ? new Date(this.resolveTermsDate(b.terms)!).getTime() : 0;
        return (tA - tB) * dir;
      }

      // String columns
      const valA = ((a as any)[col] || '').toString().toLowerCase();
      const valB = ((b as any)[col] || '').toString().toLowerCase();
      if (valA < valB) return -1 * dir;
      if (valA > valB) return  1 * dir;
      return 0;
    });

    this.dataSource = data;
  }

  sortBy(column: string): void {
    if (this.sortColumn === column) {
      if (this.sortDirection === 'asc')       { this.sortDirection = 'desc'; }
      else if (this.sortDirection === 'desc') { this.sortDirection = ''; this.sortColumn = ''; }
      else                                    { this.sortDirection = 'asc'; }
    } else {
      this.sortColumn    = column;
      this.sortDirection = 'asc';
    }
    this.applySort();
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column || this.sortDirection === '') {
      return 'bi-arrow-down-up sort-icon-neutral';
    }
    return this.sortDirection === 'asc'
      ? 'bi-arrow-up sort-icon-active'
      : 'bi-arrow-down sort-icon-active';
  }

  // ═══════════════════════════════════════════════════════════
  //  SEARCH
  // ═══════════════════════════════════════════════════════════

  onSearch(): void { this.applyFilter(); }

  clearSearch(): void {
    this.searchQuery = '';
    this.applyFilter();
  }

  // ═══════════════════════════════════════════════════════════
  //  PAGINATION
  // ═══════════════════════════════════════════════════════════

  getPage(): Consumer[] {
    const start = this.pageIndex * this.pageSize;
    return this.dataSource.slice(start, start + this.pageSize);
  }

  getTotalPages(): number {
    return Math.max(1, Math.ceil(this.dataSource.length / this.pageSize));
  }

  getPageNumbers(): number[] {
    const total   = this.getTotalPages();
    const current = this.pageIndex + 1;
    const maxVis  = 5;
    let start = Math.max(1, current - Math.floor(maxVis / 2));
    let end   = Math.min(total, start + maxVis - 1);
    if (end - start < maxVis - 1) start = Math.max(1, end - maxVis + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  getEndIndex(): number {
    return Math.min((this.pageIndex + 1) * this.pageSize, this.dataSource.length);
  }

  getPaginationRange(): string {
    if (this.dataSource.length === 0) return '0-0 of 0 items';
    return `${this.pageIndex * this.pageSize + 1}-${this.getEndIndex()} of ${this.dataSource.length} items`;
  }

  goToFirstPage(): void { if (this.pageIndex !== 0) this.pageIndex = 0; }
  goToPrevPage(): void  { if (this.pageIndex > 0) this.pageIndex--; }
  goToNextPage(): void  { if (this.pageIndex < this.getTotalPages() - 1) this.pageIndex++; }
  goToLastPage(): void  { this.pageIndex = this.getTotalPages() - 1; }
  goToPage(index: number): void {
    if (index >= 0 && index < this.getTotalPages()) this.pageIndex = index;
  }

  // ═══════════════════════════════════════════════════════════
  //  HELPERS
  // ═══════════════════════════════════════════════════════════

  formatDate(date: string | null): string {
    if (!date) return '-';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: '2-digit'
    });
  }

  formatDateTime(date: string | null): string {
    if (!date) return '-';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    const datePart = d.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: '2-digit'
    });
    const timePart = d.toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit', hour12: true
    });
    return `${datePart} | ${timePart}`;
  }

  /**
   * Extracts a date string from the `terms` field.
   * Handles: null, {}, "2025-11-07T...", { date: "..." }, { signed_at: "..." }
   */
  resolveTermsDate(terms: any): string | null {
    if (!terms) return null;
    // Plain date string
    if (typeof terms === 'string' && terms.trim()) return terms;
    // Object — look for common date keys
    if (typeof terms === 'object') {
      const dateKey = ['date', 'signed_at', 'signedAt', 'accepted_at', 'acceptedAt', 'timestamp']
        .find(k => terms[k]);
      if (dateKey) return terms[dateKey];
      // If object has any keys at all with a truthy value, try the first one
      const firstVal = Object.values(terms).find(v => v && typeof v === 'string');
      if (firstVal) return firstVal as string;
    }
    return null;
  }

  /** Display helper used in the template */
  getTermsDate(terms: any): string {
    const raw = this.resolveTermsDate(terms);
    return raw ? this.formatDateTime(raw) : 'Not Signed';
  }

  getStatusClass(status: string): string {
    const map: { [key: string]: string } = {
      'active':   'status-active',
      'inactive': 'status-inactive',
      'pending':  'status-pending',
    };
    return map[(status || '').toLowerCase()] || 'status-default';
  }

  toggleActionMenu(uid: string, event: Event): void {
    event.stopPropagation();

    this.activeMenuUid =
    this.activeMenuUid === uid ? null : uid;
  }

  // Row -> client history screen (appointments, payments, refunds, activity).
  //
  // The list payload puts the coffer id in `id` and the record id in
  // `client_id`; the history APIs key off the record id, so route on that.
  openClientHistory(client: any): void {
    if (!client?.client_id) {
      this.toastr.error('This client has no id to open history for');
      return;
    }

    this.router.navigate(['/admin/lexiz-clients', client.client_id]);
  }

  openAccountTypeModal(client: any): void {
    this.selectedClient = client;
    this.selectedAccountType = client.account_type || 'client';
    this.showAccountTypeModal = true;
  }

  updateAccountType(): void {
    if (!this.selectedClient) return;

    //console.log('selectedClient', this.selectedClient)
    const payload = {
      //uid: this.selectedClient.id,
      account_type: this.selectedAccountType
    };

    this.clientListService.updateType(this.selectedClient.id, payload)
      .subscribe({
        next: () => {
          this.toastr.success('Account Type Updated Successfully');
          this.showAccountTypeModal = false;
          this.loadClients();
        },
        error: () => {
          this.toastr.error('Failed to update Account Type');
        }
      });
  }

  closeAccountTypeModal(): void {
    this.showAccountTypeModal = false;
  }

}